import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Badge, Spinner, ProgressBar, Alert } from 'react-bootstrap';
import api from '../../api/api';
import { showToast } from '../../utils/toast';

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
const SCOPES = 'https://www.googleapis.com/auth/drive.readonly';

const SelfContentPicker = ({ project, slug, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [uploadingTopic, setUploadingTopic] = useState(null);
  const [topicAssets, setTopicAssets] = useState({}); // {topicId: [files]}

  // --- 1. Load Existing Topic Assets ---
  const fetchTopicAssets = async () => {
    if (!slug || !project.id) return;
    try {
      const res = await api.get(`/tenant/${slug}/projects/${project.id}/self-content-status`);
      setTopicAssets(res.data);
    } catch (err) { console.error("Failed to load topic assets", err); }
  };
  useEffect(() => { fetchTopicAssets(); }, [project.id, slug]);

  // --- 2. Google Picker Logic (Contextual for Topic) ---
  const handlePickerAuth = (topicId, topicTitle) => {
    setUploadingTopic(topicId);
    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: CLIENT_ID,
      scope: SCOPES,
      callback: (response) => {
        if (response.access_token) {
          openPicker(response.access_token, topicId, topicTitle);
        }
      },
    });
    client.requestAccessToken();
  };

  const openPicker = (token, topicId, topicTitle) => {
    const picker = new window.google.picker.PickerBuilder()
      .addView(window.google.picker.ViewId.DOCS) // Filter for Docs/PPTs in your actual logic
      .setOAuthToken(token)
      .setDeveloperKey(API_KEY)
      .setCallback(async (data) => {
        if (data.action === window.google.picker.Action.PICKED) {
          const doc = data.docs[0];
          await processTopicUpload(doc, token, topicId, topicTitle);
        }
      })
      .build();
    picker.setVisible(true);
  };

    // --- Inside SelfContentPicker.js ---
const processTopicUpload = async (doc, token, topicId, topicTitle) => {
  setLoading(true);
  try {
    const formData = new FormData();
    
    // ✅ Ensure types match (topic_id must be an integer string)
    // If topic.id is "101_t0", you must extract the numeric part or send the raw ID
    const numericTopicId = topicId.includes('_t') ? topicId.split('_t')[0] : topicId;

    formData.append('topic_id', parseInt(numericTopicId)); 
    formData.append('topic_title', topicTitle);
    formData.append('file_id', doc.id);
    formData.append('file_name', doc.name);
    
    // ✅ Generate a hash if missing; backend requires 'file_hash'
    const fileHash = doc.id + "_" + Date.now(); 
    formData.append('file_hash', fileHash);
    
    formData.append('oauth_token', token);

    // ✅ constructing the path with the verified slug
    await api.post(`/tenant/${slug}/projects/${project.id}/ingest-topic-content`, formData);
    
    fetchTopicAssets();
    onRefresh(); 
  } catch (err) {
    // ✅ Improved error logging to see the actual 422 validation details
    const errorDetail = err.response?.data?.detail;
    const errorMessage = typeof errorDetail === 'object' 
      ? JSON.stringify(errorDetail) 
      : (errorDetail || "Unknown error");
      
    showToast("Asset ingestion failed: " + errorMessage, 'error');
  } finally {
    setLoading(false);
    setUploadingTopic(null);
  }
};

  // --- 3. Rendering the Topic List ---
    const allTopics = (project.curriculum_draft || []).flatMap(mod => 
    (mod.children || []).flatMap(unit => 
      (unit.topics || []).map((topic, tIdx) => ({
        // Create a unique ID for the topic to track assets
        id: `${unit.unit_id}_t${tIdx}`, 
        title: typeof topic === 'string' ? topic : (topic.title || topic.topic_title),
        unit_id: unit.unit_id,
        unit_title: unit.title
      }))
    )
  );

  const completionRate = Math.round((Object.keys(topicAssets).length / allTopics.length) * 100);

  return (
    <Card className="border-0 shadow-sm mt-3">
      <Card.Header className="bg-info text-white d-flex justify-content-between align-items-center">
        <strong className="small text-uppercase">Topic-Wise Faculty Content Ingestion</strong>
        <Badge bg="light" text="dark">{completionRate}% Grounded</Badge>
      </Card.Header>
      <Card.Body>
        <Alert variant="info" className="small py-2 mb-3" role="status">
          <i className="bi bi-info-circle me-2"></i>
          <strong>Full Manual Constraint:</strong> Each topic must have at least 1 faculty asset (max 3) before CKB can be generated.
        </Alert>

        <ProgressBar now={completionRate} variant="info" className="mb-4" style={{height: '10px'}} />

        <Table responsive hover size="sm" className="align-middle">
          <thead className="table-light small">
            <tr>
              <th>Topic Structure</th>
              <th scope="col" className="text-center">Assets (Max 3)</th>
              <th scope="col" className="text-end">Action</th>
            </tr>
          </thead>
          <tbody>
            {allTopics.map((topic) => {
              const assets = topicAssets[topic.id] || [];
              const isMaxed = assets.length >= 3;
              const isProcessing = uploadingTopic === topic.id;

              return (
                <tr key={topic.id}>
                  <td className="small fw-bold">{topic.title}</td>
                  <td className="text-center">
                    {assets.length > 0 ? (
                      assets.map((a, idx) => (
                        <Badge key={idx} bg="success" className="me-1" title={a.fileName}>
                          <i className="bi bi-file-earmark-check"></i>
                        </Badge>
                      ))
                    ) : (
                      <Badge bg="warning" text="dark">Missing Content</Badge>
                    )}
                  </td>
                  <td className="text-end">
                    <Button 
                      size="sm" 
                      variant={isMaxed ? "outline-secondary" : "outline-info"}
                      disabled={isMaxed || isProcessing}
                      onClick={() => handlePickerAuth(topic.id, topic.title)}
                    >
                      {isProcessing ? <Spinner size="sm" animation="border" aria-hidden="true" /> : <><i className="bi bi-plus-lg me-1"></i>Add Content</>}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

export default SelfContentPicker;
